/**
 * New User Badge Component
 * Badge with dropdown menu to "Ignore new tag"
 * Displays "New" badge that when clicked shows dropdown option
 */

import React, { useState, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, Pressable } from 'react-native';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';

interface NewUserBadgeProps {
  onIgnore: () => void;
}

const NewUserBadge: React.FC<NewUserBadgeProps> = ({ onIgnore }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [badgeLayout, setBadgeLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const badgeRef = useRef<View>(null);

  const handleBadgePress = () => {
    if (badgeRef.current) {
      badgeRef.current.measure((x, y, width, height, pageX, pageY) => {
        setBadgeLayout({ x: pageX, y: pageY, width, height });
        setShowDropdown(true);
      });
    }
  };

  const handleIgnore = () => {
    setShowDropdown(false);
    onIgnore();
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity 
        ref={badgeRef}
        style={styles.badge}
        onPress={handleBadgePress}
        activeOpacity={0.8}
      >
        <Text style={styles.badgeText}>New</Text>
        <Text style={styles.chevron}>▼</Text>
      </TouchableOpacity>

      {showDropdown && (
        <Modal
          transparent
          visible={showDropdown}
          onRequestClose={() => setShowDropdown(false)}
          animationType="fade"
        >
          <Pressable 
            style={styles.overlay} 
            onPress={() => setShowDropdown(false)}
          >
            <View 
              style={[
                styles.dropdown,
                { 
                  top: badgeLayout.y + badgeLayout.height + 4,
                  left: badgeLayout.x,
                }
              ]}
            >
              <TouchableOpacity 
                style={styles.dropdownItem}
                onPress={handleIgnore}
                activeOpacity={0.7}
              >
                <Text style={styles.dropdownText}>Ignore new tag</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Modal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 2,
    height: 22,
    backgroundColor: AdminColors.blue75,
    borderRadius: 12,
    gap: 3,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: AdminColors.blue900,
    fontFamily: FontFamily.bold,
  },
  chevron: {
    fontSize: 8,
    color: AdminColors.blue900,
    opacity: 0.6,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  dropdown: {
    position: 'absolute',
    minWidth: 150,
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
    paddingVertical: 3,
  },
  dropdownItem: {
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  dropdownText: {
    fontSize: 12,
    lineHeight: 16,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },
});

export default NewUserBadge;
